import { CustomerTeamRole } from '@/types/customer-teams';
import { apiClient } from '@/services/api-client';

interface JsonApiResource<TAttributes = Record<string, unknown>> {
  id: string;
  type: string;
  attributes?: TAttributes;
  relationships?: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

interface JsonApiResponse<T = unknown> {
  data: T;
  included?: JsonApiResource[];
  meta?: Record<string, unknown>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length ? value.trim() : undefined;
}

function resolveAttributes(resource: JsonApiResource): Record<string, unknown> {
  if (isRecord(resource.attributes)) {
    return resource.attributes as Record<string, unknown>;
  }

  if (!isRecord(resource)) {
    return {};
  }

  const raw = resource as unknown as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  const excluded = new Set(['id', 'type', 'attributes', 'relationships', 'meta', 'links', '_meta', '_links']);

  Object.entries(raw).forEach(([key, value]) => {
    if (!excluded.has(key) && !key.endsWith('Meta') && !key.endsWith('Links')) {
      result[key] = value;
    }
  });

  return result;
}

function normalizeRelationshipRefs(reference: unknown): Array<{ id: string; type?: string }> {
  if (!reference) {
    return [];
  }

  if (Array.isArray(reference)) {
    return reference
      .map((item) => {
        if (item && typeof item === 'object' && typeof (item as { id?: unknown }).id === 'string') {
          return {
            id: (item as { id: string }).id,
            type: typeof (item as { type?: unknown }).type === 'string' ? ((item as { type?: string }).type as string) : undefined,
          };
        }
        if (typeof item === 'string') {
          return { id: item };
        }
        return undefined;
      })
      .filter((ref): ref is { id: string; type?: string } => Boolean(ref));
  }

  if (typeof reference === 'string') {
    return [{ id: reference }];
  }

  if (isRecord(reference)) {
    if (typeof reference.id === 'string') {
      return [{ id: reference.id as string, type: typeof reference.type === 'string' ? (reference.type as string) : undefined }];
    }

    if (reference.data !== undefined) {
      return normalizeRelationshipRefs(reference.data);
    }
  }

  return [];
}

function resolveRelationship(resource: JsonApiResource, key: string): { data?: unknown; meta?: Record<string, unknown> } | undefined {
  if (resource.relationships && key in resource.relationships) {
    const relationship = resource.relationships[key];
    if (isRecord(relationship)) {
      return relationship as { data?: unknown; meta?: Record<string, unknown> };
    }
  }

  const raw = resource as unknown as Record<string, unknown>;
  const dataCandidate = raw[key];
  const metaCandidate = raw[`${key}Meta`];

  if (dataCandidate !== undefined || metaCandidate !== undefined) {
    return {
      data: dataCandidate,
      meta: isRecord(metaCandidate) ? (metaCandidate as Record<string, unknown>) : undefined,
    };
  }

  const resourceMeta = resource.meta;
  if (isRecord(resourceMeta) && isRecord(resourceMeta[key])) {
    return {
      data: dataCandidate,
      meta: resourceMeta[key] as Record<string, unknown>,
    };
  }

  const legacyMeta = raw._meta;
  if (isRecord(legacyMeta) && isRecord(legacyMeta[key])) {
    return {
      data: dataCandidate,
      meta: legacyMeta[key] as Record<string, unknown>,
    };
  }

  return undefined;
}

function findIncluded(response: JsonApiResponse, type: string, id: string): JsonApiResource | undefined {
  return response.included?.find((item) => item.type === type && item.id === id);
}

export interface TeamInvitationDetails {
  id: string;
  email: string;
  role: CustomerTeamRole;
  roleLabel?: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  statusLabel?: string;
  message?: string;
  invitedAt?: string;
  expiresAt?: string;
  respondedAt?: string;
  inviterName?: string;
  teamId?: string;
  teamName?: string;
  providerName?: string;
  language?: string;
}

function readInvitation(resource: JsonApiResource, response: JsonApiResponse): TeamInvitationDetails {
  const attributes = resolveAttributes(resource);

  const roleKeyRaw =
    toOptionalString(attributes.role) ??
    toOptionalString(attributes.role_key) ??
    'member';
  const normalizedRoleKey = roleKeyRaw ? roleKeyRaw.toLowerCase() : 'member';
  const role = (['owner', 'admin', 'member'].includes(normalizedRoleKey)
    ? normalizedRoleKey
    : 'member') as CustomerTeamRole;

  const statusKeyRaw =
    toOptionalString(attributes.status) ??
    toOptionalString(attributes.state) ??
    'pending';
  const normalizedStatusKey = statusKeyRaw ? statusKeyRaw.toLowerCase() : 'pending';
  const status = (['pending', 'accepted', 'declined', 'expired'].includes(normalizedStatusKey)
    ? normalizedStatusKey
    : 'pending') as TeamInvitationDetails['status'];

  const statusLabel =
    toOptionalString(attributes.status_label) ??
    toOptionalString(attributes.statusLabel) ??
    (status === 'pending' ? 'Pending invitation' : status.charAt(0).toUpperCase() + status.slice(1));

  const invitedByRelationship = resolveRelationship(resource, 'invited_by');
  const invitedByRefs = normalizeRelationshipRefs(invitedByRelationship?.data);
  const invitedByResource = invitedByRefs.length
    ? findIncluded(response, invitedByRefs[0].type ?? 'customers', invitedByRefs[0].id)
    : undefined;
  const invitedByAttributes = invitedByResource ? resolveAttributes(invitedByResource) : {};
  const invitedByMeta = invitedByRelationship?.meta ?? {};

  const inviterName =
    toOptionalString(invitedByAttributes.name) ??
    toOptionalString(invitedByAttributes.full_name) ??
    toOptionalString(invitedByAttributes.fullName) ??
    (isRecord(invitedByMeta)
      ? toOptionalString(invitedByMeta.name) ??
        toOptionalString(invitedByMeta.full_name) ??
        toOptionalString(invitedByMeta.fullName)
      : undefined);

  const teamRelationship = resolveRelationship(resource, 'team');
  const teamRefs = normalizeRelationshipRefs(teamRelationship?.data);
  const teamResource = teamRefs.length
    ? findIncluded(response, teamRefs[0].type ?? 'teams', teamRefs[0].id)
    : undefined;
  const teamAttributes = teamResource ? resolveAttributes(teamResource) : {};
  const teamMeta = teamRelationship?.meta ?? {};

  const teamName =
    toOptionalString(teamAttributes.name) ??
    toOptionalString(teamAttributes.display_name) ??
    toOptionalString(teamAttributes.displayName) ??
    (isRecord(teamMeta)
      ? toOptionalString(teamMeta.name) ?? toOptionalString(teamMeta.display_name) ?? toOptionalString(teamMeta.displayName)
      : undefined);

  const providerRelationship = resolveRelationship(resource, 'provider');
  const providerRefs = normalizeRelationshipRefs(providerRelationship?.data);
  const providerResource = providerRefs.length
    ? findIncluded(response, providerRefs[0].type ?? 'providers', providerRefs[0].id)
    : undefined;
  const providerAttributes = providerResource ? resolveAttributes(providerResource) : {};
  const providerMeta = providerRelationship?.meta ?? {};

  const providerName =
    toOptionalString(providerAttributes.name) ??
    toOptionalString(providerAttributes.display_name) ??
    (isRecord(providerMeta)
      ? toOptionalString(providerMeta.name) ?? toOptionalString(providerMeta.display_name)
      : undefined);

  const metadata = isRecord(attributes.metadata) ? (attributes.metadata as Record<string, unknown>) : undefined;
  const invitationLanguage =
    toOptionalString(attributes.language) ?? (metadata ? toOptionalString(metadata.language) : undefined);

  return {
    id: resource.id,
    email: String(attributes.email ?? ''),
    role,
    roleLabel:
      toOptionalString(attributes.role_label) ??
      toOptionalString(attributes.roleLabel) ??
      (role === 'owner' ? 'Team Owner' : role === 'admin' ? 'Team Admin' : 'Team Member'),
    status,
    statusLabel,
    message: toOptionalString(attributes.message),
    invitedAt:
      toOptionalString(attributes.created_at) ??
      toOptionalString(attributes.createdAt) ??
      toOptionalString(attributes.invited_at) ??
      toOptionalString(attributes.invitedAt),
    expiresAt: toOptionalString(attributes.expires_at) ?? toOptionalString(attributes.expiresAt),
    respondedAt: toOptionalString(attributes.responded_at) ?? toOptionalString(attributes.respondedAt),
    inviterName,
    teamId: teamRefs[0]?.id,
    teamName,
    providerName,
    language: invitationLanguage,
  };
}

export async function getTeamInvitation(token: string) {
  const response = await apiClient.get<JsonApiResponse<JsonApiResource>>(`/api/v1/invitations/${token}`);
  const payload = response.data as JsonApiResponse<JsonApiResource> | Record<string, unknown>;

  let resource: JsonApiResource | undefined;
  let included: JsonApiResource[] | undefined;

  if (isRecord(payload) && 'data' in payload && payload.data) {
    resource = payload.data as JsonApiResource;
    included = Array.isArray(payload.included) ? (payload.included as JsonApiResource[]) : undefined;
  } else if (isRecord(payload)) {
    resource = {
      id: String(payload.id ?? token),
      type: String(payload.type ?? 'team-invitations'),
      attributes: payload,
    };
    included = undefined;
  }

  if (!resource) {
    throw new Error('Invitation payload missing data.');
  }

  return readInvitation(resource, { data: resource, included });
}

export async function acceptTeamInvitation({
  token,
  email,
  language,
}: {
  token: string;
  email?: string;
  language?: string;
}) {
  const body: Record<string, string> = {};
  if (email) {
    body.email = email;
  }
  if (language) {
    body.language = language;
  }
  const response = await apiClient.post<{ meta?: Record<string, unknown> }, Record<string, string>>(
    `/api/v1/invitations/${token}/accept`,
    body
  );
  const meta = response.data.meta ?? {};
  return {
    message: toOptionalString((meta as Record<string, unknown>).message) ?? 'Invitation accepted successfully',
  };
}

export async function declineTeamInvitation({
  token,
  email,
  reason,
  language,
}: {
  token: string;
  email?: string;
  reason?: string;
  language?: string;
}) {
  const payload: Record<string, string> = {};
  if (email) {
    payload.email = email;
  }
  if (reason && reason.trim().length) {
    payload.reason = reason.trim();
  }
  if (language) {
    payload.language = language;
  }

  const response = await apiClient.post<{ meta?: Record<string, unknown> }, Record<string, string>>(
    `/api/v1/invitations/${token}/decline`,
    payload
  );
  const meta = response.data.meta ?? {};
  return {
    message: toOptionalString((meta as Record<string, unknown>).message) ?? 'Invitation declined successfully',
  };
}
